import styled from 'styled-components';
import {
    Attribution,
    AttributionLink,
    Avatar,
    Box,
    BoxProps,
    Inline,
    Link,
    Text,
} from '@99designs/emu';
import { boxShadows, colors } from '@99designs/emu/settings';
import { formatMessage } from '@99designs/frontend-components/i18n';

const DesignWrapper = styled(Box)`
    box-shadow: ${boxShadows.low};
`;

const AvatarLink = styled(Link)`
    display: inline-block;
    border-bottom: none;
`;

export interface DesignCardProps {
    src: string;
    alt?: string;
    displayName: string;
    profileUrl: string;
    avatarUrl: string;
    width?: BoxProps['width'];
    height?: BoxProps['height'];
    marginBottom?: BoxProps['marginBottom'];
    truncateDisplayName?: boolean;
    displayNameWidth?: BoxProps['width'];
    onClick?: (e: React.MouseEvent) => void;
}

export function DesignCard({
    src,
    alt,
    displayName,
    profileUrl,
    avatarUrl,
    width,
    height,
    marginBottom,
    truncateDisplayName = false,
    displayNameWidth,
    onClick,
}: DesignCardProps) {
    return (
        <Box>
            <DesignWrapper
                marginBottom={marginBottom || ['0.5bu', '1bu', '1bu', '1bu']}
                width={width || ['160px', '290px', '290px', '290px']}
                height={height || ['160px', '290px', '290px', '290px']}
                background={colors.grey200}
            >
                <img
                    src={src}
                    className="lazyload"
                    alt={
                        alt
                            ? alt
                            : formatMessage(__('Design by {displayName}'), {
                                  displayName,
                              })
                    }
                />
            </DesignWrapper>
            <Box paddingBottom="0.5bu">
                <Inline space={['0.5bu', '1bu', '1bu', '1bu']} noWrap>
                    <AvatarLink
                        href={profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={formatMessage(__('Profile for {displayName}'), {
                            displayName,
                        })}
                    >
                        <Avatar url={avatarUrl} size="extra-small" />
                    </AvatarLink>

                    {truncateDisplayName ? (
                        <Box width={displayNameWidth || ['90px', '110px', '140px']}>
                            <Text as="p" truncate={true}>
                                <DesignCardAttribution
                                    profileUrl={profileUrl}
                                    displayName={displayName}
                                    onClick={onClick}
                                />
                            </Text>
                        </Box>
                    ) : (
                        <DesignCardAttribution
                            profileUrl={profileUrl}
                            displayName={displayName}
                            onClick={onClick}
                        />
                    )}
                </Inline>
            </Box>
        </Box>
    );
}

type DesignCardAttributionParams = {
    profileUrl: string;
    displayName: string;
    onClick?: (e: React.MouseEvent) => void;
};

const DesignCardAttribution = ({
    profileUrl,
    displayName,
    onClick,
}: DesignCardAttributionParams) => {
    return (
        <Attribution>
            <AttributionLink
                href={profileUrl}
                aria-label={formatMessage(__('Profile for {displayName}'), {
                    displayName,
                })}
                onClick={onClick}
            >
                {displayName}
            </AttributionLink>
        </Attribution>
    );
};
